/**
 * Settings Component
 * Three-tab layout: Settings · Stream · Interact
 */

import React, { useState } from 'react';
import { useUIStore, useSettingsStore } from '@flow-studio/store';
import { apiConfig } from '@flow-studio/api';
import { MoonIcon, SunIcon, WrenchIcon } from '@bizfirst/common-react';
import { Input, Select } from '../Shared/FormUI';
import { isDevelopment } from 'flow-studio-core';
import { type ModuleItem, getModuleGroups } from '../../modules/moduleRegistry';
import { ModuleCard } from '../Modules/ModuleCard';

export type Tab = 'settings' | 'stream' | 'interact';

export interface SettingsProps {
  defaultTab?: Tab;
  onModuleSelect?: (item: ModuleItem, fromTab: Tab) => void;
}

type SettingsSubTab = 'general' | 'nodes' | 'registry';

const NODE_TEMPLATE_REPO_OPTIONS = [
  { value: 'default',       label: 'Default' },
  { value: 'BizFirstGlobal', label: 'BizFirst Global Nodes' },
];

interface RegistryExecutorItem {
  nodeTypeCode: string;
  executorType: string;
  executorAssembly: string;
}

interface RegistryResponse {
  success: boolean;
  count: number;
  data: RegistryExecutorItem[];
}

export const Settings: React.FC<SettingsProps> = ({ defaultTab, onModuleSelect }) => {
  const { theme, setTheme, showToast } = useUIStore();
  const { settings, updateSettings } = useSettingsStore();
  const [activeTab, setActiveTab] = useState<Tab>(defaultTab ?? 'settings');
  const [settingsSubTab, setSettingsSubTab] = useState<SettingsSubTab>('general');

  // Registry tab state
  const [registryData, setRegistryData] = useState<RegistryExecutorItem[] | null>(null);
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [registryFilter, setRegistryFilter] = useState('');

  const loadRegistry = async () => {
    setRegistryLoading(true);
    setRegistryError(null);
    try {
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (apiConfig.tenantId) headers['X-Tenant-ID'] = String(apiConfig.tenantId);

      const res = await fetch(
        `${apiConfig.baseUrl.replace(/\/$/, '')}/api/v1/process-engine/registry/executors`,
        { headers }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
      const json: RegistryResponse = await res.json();
      setRegistryData(json.data ?? []);
    } catch (err: any) {
      setRegistryError(err?.message ?? 'Failed to load registry');
    } finally {
      setRegistryLoading(false);
    }
  };
  const moduleGroups = getModuleGroups();
  const isDark = theme === 'dark';

  // ── Shared styles ──────────────────────────────────────────────────────────

  const sectionCard: React.CSSProperties = {
    background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? '1px solid rgba(58, 140, 69, 0.2)' : '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
  };

  const sectionTitle: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 600,
    color: isDark ? '#f8fafc' : '#111827',
    margin: 0,
    marginBottom: '16px',
  };

  const inputHint: React.CSSProperties = {
    fontSize: '12px',
    color: isDark ? '#94a3b8' : '#64748b',
    marginTop: '4px',
  };

  // ── Tab definitions ────────────────────────────────────────────────────────

  const tabs: { id: Tab; label: string; accent: string }[] = [
    { id: 'settings', label: 'Settings',  accent: '#3A8C45' },
    { id: 'stream',   label: 'Stream',    accent: '#3A8C45' },
    { id: 'interact', label: 'Interact',  accent: '#3A8C45' },
  ];

  // ── Module cards renderer (shared by Stream + Interact tabs) ───────────────

  const renderModuleTab = (source: 'edge-stream' | 'edge-interact') => {
    const groups = moduleGroups.filter(g => g.sourceModule === source);
    const label  = source === 'edge-stream' ? 'EdgeStream DevTools' : 'EdgeInteract Explorer';

    return (
      <div style={{ padding: '28px 32px 28px 28px', overflow: 'auto', height: '100%' }}>
        {/* Tab header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <h2 style={{ fontSize: '22px', fontWeight: 700, color: isDark ? '#f8fafc' : '#111827', margin: 0 }}>
              {label}
            </h2>
          </div>
          <p style={{ fontSize: '14px', color: isDark ? '#64748b' : '#94a3b8', margin: 0 }}>
            Click a card to open the module inside Flow Studio
          </p>
        </div>

        {/* Groups */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {groups.map((group) => (
            <div key={group.id}>
              <div style={{
                fontSize: '11px',
                fontWeight: 700,
                color: isDark ? '#475569' : '#9ca3af',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '12px',
                paddingBottom: '8px',
                borderBottom: isDark ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f1f5f9',
              }}>
                {group.label}
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: '12px',
              }}>
                {group.items.map((item) => (
                  <ModuleCard
                    key={item.id}
                    item={item}
                    onClick={(item) => onModuleSelect?.(item, activeTab)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ── Tab bar ── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        padding: '0 32px 0 28px',
        borderBottom: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
        background: isDark ? 'rgba(2,6,23,0.4)' : 'rgba(255,255,255,0.6)',
        flexShrink: 0,
      }}>
        {tabs.map(({ id, label, accent }) => {
          const isActive = activeTab === id;
          return (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                padding: '14px 20px',
                background: 'transparent',
                border: 'none',
                borderBottom: isActive
                  ? `2px solid ${accent}`
                  : '2px solid transparent',
                color: isActive
                  ? accent
                  : isDark ? '#64748b' : '#94a3b8',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s ease',
                marginBottom: '-1px',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                if (!isActive) e.currentTarget.style.color = isDark ? '#94a3b8' : '#64748b';
              }}
              onMouseLeave={(e) => {
                if (!isActive) e.currentTarget.style.color = isDark ? '#64748b' : '#94a3b8';
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* ── Tab content ── */}
      <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>

        {/* Settings tab */}
        {activeTab === 'settings' && (
          <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* ── Settings sub-tab bar ── */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              padding: '0 32px 0 28px',
              borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
              background: isDark ? 'rgba(2,6,23,0.25)' : 'rgba(249,250,251,0.8)',
              flexShrink: 0,
            }}>
              {(['general', 'nodes', 'registry'] as SettingsSubTab[]).map((id) => {
                const isActive = settingsSubTab === id;
                const label = id === 'general' ? 'General' : id === 'nodes' ? 'Nodes' : 'Registry';
                return (
                  <button
                    key={id}
                    onClick={() => { setSettingsSubTab(id); if (id === 'registry' && !registryData && !registryLoading) loadRegistry(); }}
                    style={{
                      padding: '10px 16px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: isActive ? '2px solid #3A8C45' : '2px solid transparent',
                      color: isActive ? '#3A8C45' : isDark ? '#64748b' : '#94a3b8',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: isActive ? 600 : 400,
                      transition: 'all 0.15s ease',
                      marginBottom: '-1px',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.color = isDark ? '#94a3b8' : '#64748b'; }}
                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.color = isDark ? '#64748b' : '#94a3b8'; }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* ── Sub-tab content ── */}
            <div className="settings-subtab-scroll" style={{ flex: 1, overflow: 'auto', padding: '32px 32px 32px 28px' }}>
              <div className="settings-content-area" style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '800px' }}>

                {/* ── GENERAL sub-tab ── */}
                {settingsSubTab === 'general' && (
                  <>
                    {/* Appearance */}
                    <div style={sectionCard}>
                      <h2 style={sectionTitle}>Appearance</h2>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 500, color: isDark ? '#f8fafc' : '#111827', marginBottom: '4px' }}>Theme</div>
                          <div style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b' }}>Choose between light and dark mode</div>
                        </div>
                        {/* Segmented control */}
                        <div style={{
                          display: 'flex',
                          background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                          borderRadius: '10px',
                          padding: '3px',
                          gap: '2px',
                          border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                        }}>
                          {([
                            { mode: 'light' as const, icon: <SunIcon size={15} />, label: 'Light' },
                            { mode: 'dark'  as const, icon: <MoonIcon size={15} />, label: 'Dark' },
                          ] as const).map(({ mode, icon, label }) => {
                            const isSelected = theme === mode;
                            return (
                              <button
                                key={mode}
                                onClick={() => {
                                  setTheme(mode);
                                  showToast('success', `Theme changed to ${mode} mode`);
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  padding: '7px 16px',
                                  borderRadius: '8px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: isSelected ? 600 : 400,
                                  background: isSelected
                                    ? (isDark ? 'rgba(58,140,69,0.25)' : '#ffffff')
                                    : 'transparent',
                                  color: isSelected ? '#3A8C45' : (isDark ? '#64748b' : '#9ca3af'),
                                  boxShadow: isSelected
                                    ? (isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.1)')
                                    : 'none',
                                  transition: 'all 0.15s ease',
                                }}
                              >
                                {icon}
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* API Configuration */}
                    <div style={sectionCard}>
                      <h2 style={sectionTitle}>API Configuration</h2>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: isDark ? '#f8fafc' : '#111827', marginBottom: '8px' }}>API Base URL</label>
                          <Input type="text" defaultValue={apiConfig.baseUrl} readOnly style={{ fontFamily: 'monospace', color: isDark ? '#94a3b8' : '#64748b' }} />
                          <div style={inputHint}>Configured in .env file (VITE_API_BASE_URL)</div>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: isDark ? '#f8fafc' : '#111827', marginBottom: '8px' }}>Request Timeout</label>
                          <Input type="text" defaultValue={`${(import.meta as any).env?.VITE_API_TIMEOUT || '30000'}ms`} readOnly style={{ fontFamily: 'monospace', color: isDark ? '#94a3b8' : '#64748b' }} />
                          <div style={inputHint}>Configured in .env file (VITE_API_TIMEOUT)</div>
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: isDark ? '#f8fafc' : '#111827', marginBottom: '8px' }}>Tenant ID</label>
                          <Input type="text" defaultValue={(import.meta as any).env?.VITE_TENANT_ID || '1'} readOnly style={{ fontFamily: 'monospace', color: isDark ? '#94a3b8' : '#64748b' }} />
                          <div style={inputHint}>Configured in .env file (VITE_TENANT_ID)</div>
                        </div>
                      </div>
                    </div>

                    {/* Developer Mode */}
                    {isDevelopment() && (
                      <div style={{
                        background: isDark ? 'rgba(245, 158, 11, 0.08)' : 'rgba(245, 158, 11, 0.06)',
                        border: isDark ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(245, 158, 11, 0.4)',
                        borderRadius: '12px',
                        padding: '24px',
                        boxShadow: isDark ? '0 4px 6px -1px rgba(0, 0, 0, 0.3)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                      }}>
                        <h2 style={{ fontSize: '18px', fontWeight: 600, color: isDark ? '#fbbf24' : '#d97706', margin: 0, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span><WrenchIcon size={16} /></span>
                          <span>Developer Mode</span>
                        </h2>
                        <p style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b', margin: 0 }}>
                          The application is currently running in development mode. This section is only visible in non-production environments.
                        </p>
                      </div>
                    )}

                    {/* About */}
                    <div style={sectionCard}>
                      <h2 style={sectionTitle}>About</h2>
                      <div style={{ fontSize: '14px', color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.6 }}>
                        <p style={{ margin: '0 0 8px 0' }}><strong>BizFirst AI Studio</strong> - Workflow Editor Module</p>
                        <p style={{ margin: '0 0 8px 0' }}>Version: 1.0.0</p>
                        <p style={{ margin: 0 }}>A powerful workflow design and automation platform</p>
                      </div>
                    </div>
                  </>
                )}

                {/* ── REGISTRY sub-tab ── */}
                {settingsSubTab === 'registry' && (
                  <div style={sectionCard}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                      <h2 style={{ ...sectionTitle, marginBottom: 0 }}>
                        Executor Registry
                        {registryData && (
                          <span style={{ fontSize: '13px', fontWeight: 400, color: isDark ? '#64748b' : '#94a3b8', marginLeft: '10px' }}>
                            {registryData.length} registered
                          </span>
                        )}
                      </h2>
                      <button
                        onClick={loadRegistry}
                        disabled={registryLoading}
                        style={{
                          padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 500,
                          border: `1px solid ${isDark ? 'rgba(58,140,69,0.4)' : 'rgba(58,140,69,0.5)'}`,
                          background: isDark ? 'rgba(58,140,69,0.12)' : 'rgba(58,140,69,0.08)',
                          color: isDark ? '#6ee7b7' : '#16a34a',
                          cursor: registryLoading ? 'not-allowed' : 'pointer',
                          opacity: registryLoading ? 0.6 : 1,
                        }}
                      >
                        {registryLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>

                    {registryError && (
                      <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '12px 16px', color: isDark ? '#f87171' : '#dc2626', fontSize: '13px', marginBottom: '16px' }}>
                        {/* Friendly message for the user — the raw error (e.g. "HTTP 404 Not
                            Found") is a backend-availability detail, not something a Settings-page
                            visitor should have to parse; kept in `title` for anyone inspecting. */}
                        <div style={{ fontWeight: 600, marginBottom: '2px' }} title={registryError}>
                          Registry executors are unavailable right now
                        </div>
                        <div style={{ opacity: 0.85 }}>
                          Try Refresh, or check back later if the problem persists.
                        </div>
                      </div>
                    )}

                    {registryData && (
                      <>
                        <input
                          type="text"
                          placeholder="Filter by node type code or executor…"
                          value={registryFilter}
                          onChange={e => setRegistryFilter(e.target.value)}
                          style={{
                            width: '100%', padding: '7px 12px', marginBottom: '12px',
                            borderRadius: '6px', border: isDark ? '1px solid rgba(255,255,255,0.12)' : '1px solid #d1d5db',
                            background: isDark ? 'rgba(255,255,255,0.05)' : '#fff',
                            color: isDark ? '#f8fafc' : '#111827', fontSize: '13px', boxSizing: 'border-box', outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '420px', overflow: 'auto' }}>
                          {registryData
                            .filter(item =>
                              !registryFilter ||
                              item.nodeTypeCode.toLowerCase().includes(registryFilter.toLowerCase()) ||
                              item.executorType.toLowerCase().includes(registryFilter.toLowerCase()) ||
                              item.executorAssembly.toLowerCase().includes(registryFilter.toLowerCase())
                            )
                            .map(item => (
                              <div key={item.nodeTypeCode} style={{
                                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                                gap: '8px', padding: '8px 12px', borderRadius: '6px',
                                background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid #f1f5f9',
                                fontSize: '12px',
                              }}>
                                <code style={{ color: isDark ? '#6ee7b7' : '#16a34a', fontFamily: 'monospace' }}>{item.nodeTypeCode}</code>
                                <span style={{ color: isDark ? '#94a3b8' : '#374151' }}>{item.executorType}</span>
                                <span style={{ color: isDark ? '#475569' : '#9ca3af', fontSize: '11px', alignSelf: 'center' }}>{item.executorAssembly}</span>
                              </div>
                            ))
                          }
                        </div>
                      </>
                    )}

                    {!registryData && !registryLoading && !registryError && (
                      <div style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: '13px' }}>
                        Click Refresh to query the live executor registry.
                      </div>
                    )}
                  </div>
                )}

                {/* ── NODES sub-tab ── */}
                {settingsSubTab === 'nodes' && (
                  <>
                    {/* Node Template Repository */}
                    <div style={sectionCard}>
                      <h2 style={sectionTitle}>Node Template Repository</h2>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: isDark ? '#f8fafc' : '#111827', marginBottom: '8px' }}>
                          Repository URL
                        </label>
                        <Select
                          options={NODE_TEMPLATE_REPO_OPTIONS}
                          value={settings.currentNodeTemplateRepositoryUrl}
                          onChange={(e) => updateSettings({ currentNodeTemplateRepositoryUrl: e.target.value })}
                          placeholder="Select a repository"
                          className="form-select--primary"
                        />
                        <div style={{ ...inputHint, marginTop: '12px', lineHeight: 1.6 }}>
                          The Node Templates can be published by anybody in the world and a developer can point to
                          the repository path of any vendor or private party. This enables private companies to
                          manage their own nodes and define and manage and monetize their own repositories.
                        </div>
                      </div>
                    </div>
                  </>
                )}

              </div>
            </div>
          </div>
        )}

        {/* Stream tab */}
        {activeTab === 'stream' && renderModuleTab('edge-stream')}

        {/* Interact tab */}
        {activeTab === 'interact' && renderModuleTab('edge-interact')}

      </div>
    </div>
  );
};
